/**
 * useFeatureGate(feature) — pre-check de gating para el FE (NER-3161).
 *
 * Permite decidir ANTES de renderizar/click si una feature está disponible en el plan
 * actual (o grandfathered), evitando el flujo click → 403 → modal. La autoridad final
 * sigue siendo el 403 `FEATURE_GATE_REJECTED` del backend (interceptado globalmente por
 * FeatureGateProvider); este hook es un espejo optimista de `subscription.limits`.
 *
 * Uso:
 *   const { enabled, requiresUpgrade, addonAvailable, addonId } = useFeatureGate('kds');
 *   if (!enabled) return <button onClick={() => openFeatureGate({ feature: 'kdsEnabled' })}>…</button>;
 *
 * Acepta el nombre lógico (`kds`) o el flag completo (`kdsEnabled`).
 */

import { useCallback, useMemo } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { evaluateFeatureGate, openFeatureGate } from '../config/featureGates';

export default function useFeatureGate(feature) {
  const { subscription, loading } = useSubscription();

  const result = useMemo(() => evaluateFeatureGate(feature, subscription), [feature, subscription]);

  // Abre el FeatureGateModal para esta feature (pre-check, sin esperar un 403).
  const open = useCallback(() => {
    if (!result.enabled) openFeatureGate(result);
  }, [result]);

  // Mientras carga la subscription, no bloqueamos UI (evita flashes de gate falso).
  return { ...result, loading, open };
}
